import { motion } from "framer-motion";
import { Package, Wrench, Broom, Desktop, Confetti, ArrowUpRight } from "@phosphor-icons/react";
import SectionEyebrow from "@/components/shared/SectionEyebrow";

const SERVICES = [
  {
    icon: Package,
    color: "#FF7A45",
    title: "Delivery",
    desc: "Same-day delivery for food, groceries, parcels and documents — across Trinidad & Tobago, dispatched in real time.",
    tag: "Food · Grocery · Courier · Packages",
    shadow: "ih-shadow-sunset",
  },
  {
    icon: Wrench,
    color: "#14B8A6",
    title: "Handyman",
    desc: "Plumbing, electrical, AC, repairs and odd jobs from verified local professionals — booked on-demand.",
    tag: "Repairs · Installs · Maintenance",
    shadow: "ih-shadow-teal",
  },
  {
    icon: Broom,
    color: "#D4AF37",
    title: "Cleaning",
    desc: "Home, office and post-event cleaning with vetted, insured cleaning crews — flexible scheduling.",
    tag: "Residential · Commercial · Post-event",
    shadow: "ih-shadow-gold",
  },
  {
    icon: Desktop,
    color: "#5EEAD4",
    title: "Tech Support",
    desc: "Computer repair, network and Wi-Fi setup, smart home installs and on-site troubleshooting.",
    tag: "Repair · Setup · Smart Home",
    shadow: "",
  },
  {
    icon: Confetti,
    color: "#FB7185",
    title: "Events",
    desc: "Bartenders, decorators, security and event crew for parties, weddings and corporate functions.",
    tag: "Parties · Weddings · Corporate",
    shadow: "",
  },
];

export default function Services() {
  return (
    <section id="services" className="relative py-24 lg:py-32" data-testid="home-services">
      <div className="max-w-[1400px] mx-auto px-5 lg:px-10">
        <div className="grid lg:grid-cols-12 gap-10 items-end mb-14">
          <div className="lg:col-span-7 space-y-5">
            <SectionEyebrow label="What we move" color="electric" />
            <h2 className="font-display font-extrabold text-4xl lg:text-6xl text-white text-balance leading-[1.02]">
              One platform. <span className="text-white/40">Five</span> services. <br />
              <span className="text-ih-teal">Engineered for the Caribbean.</span>
            </h2>
          </div>
          <p className="lg:col-span-5 text-white/60 text-base lg:text-lg leading-relaxed">
            From last-minute deliveries in Port of Spain to weekend cleaning crews in San Fernando — IslandHop
            is the single app that connects you to vetted Caribbean pros, on demand.
          </p>
        </div>

        {/* Bento grid */}
        <div className="grid lg:grid-cols-6 gap-4">
          {SERVICES.map((s, i) => {
            const Icon = s.icon;
            const span = i === 0 ? "lg:col-span-3 lg:row-span-2" : i === 4 ? "lg:col-span-3" : "lg:col-span-2";
            return (
              <motion.div
                key={s.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 0.5, delay: i * 0.05 }}
                data-testid={`service-card-${s.title.toLowerCase().replace(/\s+/g, "-")}`}
                className={`group relative ${span} bg-ih-surface border border-ih-line rounded-2xl p-7 hover:border-white/20 transition overflow-hidden`}
              >
                <div className="absolute -top-20 -right-20 w-60 h-60 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-500" style={{ background: `radial-gradient(circle, ${s.color}33, transparent 70%)` }} />
                <div className="relative flex flex-col h-full justify-between gap-8">
                  <div className="flex items-start justify-between">
                    <span className="w-12 h-12 rounded-xl bg-black/40 border border-ih-line2 grid place-items-center" style={{ color: s.color }}>
                      <Icon weight="duotone" size={24} />
                    </span>
                    <span className="text-white/30 group-hover:text-white transition">
                      <ArrowUpRight size={20} weight="bold" />
                    </span>
                  </div>
                  <div>
                    <h3 className="font-display font-bold text-2xl text-white mb-2">{s.title}</h3>
                    <p className="text-sm text-white/60 leading-relaxed max-w-md">{s.desc}</p>
                    <p className="mt-4 font-mono text-[11px] uppercase tracking-[0.18em] text-white/40">{s.tag}</p>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
